'use client';

import { ContactPlacement, trackGaEvent } from '@/lib/analytics';
import { Z_INDEX } from '@/constants';
import { DefaultCallNumber } from '@/data/globals.data';
import { cn } from '@/lib/utils';
import { useStoreList } from '@/services/query/store.query';
import { StoreType } from '@/services/types/store.type';
import Autoplay from 'embla-carousel-autoplay';
import { useState } from 'react';
import ImageOptimizer from './image-optimizer';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from './ui/carousel';

const SideCTA = () => {
  // const [isVisible] = useState(true);
  // const [currentStoreIndex, setCurrentStoreIndex] = useState(0);

  // const pathname = usePathname();
  const [pcApi, setPcApi] = useState<CarouselApi>();

  // 매장 리스트 가져오기
  const { data: storeListData, isLoading } = useStoreList({
    limit: 9999,
    types: StoreType.DIRECT,
  });

  const stores = storeListData?.data?.stores || [];
  // const currentStore = stores[currentStoreIndex];

  const handlePrevClick = () => {
    pcApi?.scrollPrev();
    const autoplay = pcApi?.plugins().autoplay;
    if (!autoplay) return;
    autoplay.stop();
    setTimeout(() => autoplay.play(), 1000);
  };

  const handleNextClick = () => {
    pcApi?.scrollNext();
    const autoplay = pcApi?.plugins().autoplay;
    if (!autoplay) return;
    autoplay.stop();
    setTimeout(() => autoplay.play(), 1000);
  };

  // 자동 슬라이드 기능
  // useEffect(() => {
  //   if (stores.length <= 1) return; // 매장이 1개 이하면 자동 슬라이드 안함

  //   const interval = setInterval(() => {
  //     setCurrentStoreIndex((prev) => (prev < stores.length - 1 ? prev + 1 : 0));
  //   }, 3000); // 3초마다 변경

  //   return () => clearInterval(interval);
  // }, [stores.length]);

  // useEffect(() => {
  //   const toggleVisibility = () => {
  //     // 스크롤이 300px 이상 내려갔을 때 보이기 시작
  //     if (window.pageYOffset > 300) {
  //       setIsVisible(true);
  //     } else {
  //       setIsVisible(false);
  //     }
  //   };

  //   window.addEventListener('scroll', toggleVisibility);
  //   return () => window.removeEventListener('scroll', toggleVisibility);
  // }, []);

  // a[href] 가 아니라 window.open 이라 document 클릭 수집(google-analytics.tsx)에 안 잡힌다.
  // 직접 보내지 않으면 PC 우측 CTA 의 전화·카카오 클릭이 통째로 집계에서 빠진다.
  const handleCall = (phoneNumber?: string) => {
    const phone = phoneNumber || DefaultCallNumber;
    trackGaEvent('click_call', {
      link_url: `tel:${phone}`,
      placement: ContactPlacement.SIDE_CTA,
    });
    window.open(`tel:${phone}`, '_self');
  };

  const handleKakaoTalk = () => {
    // 카카오톡 상담하기 링크 (실제 카카오톡 채널 링크로 변경 필요)
    const url = 'https://pf.kakao.com/_urbdG';
    trackGaEvent('click_kakao_chat', {
      link_url: url,
      placement: ContactPlacement.SIDE_CTA,
    });
    window.open(url, '_blank');
  };

  // const handlePrevStore = () => {
  //   if (stores.length > 0) {
  //     setCurrentStoreIndex((prev) => (prev > 0 ? prev - 1 : stores.length - 1));
  //   }
  // };

  // const handleNextStore = () => {
  //   if (stores.length > 0) {
  //     setCurrentStoreIndex((prev) => (prev < stores.length - 1 ? prev + 1 : 0));
  //   }
  // };

  // if (!isVisible || isLoading) return null;
  if (isLoading) return null;

  // 현재 경로에 따라 탭 결정
  // const getTabs = (): MenuItem[] | null => {
  //   const target = menuItems.find(
  //     (item) => item.parentHref && pathname?.startsWith(item.parentHref),
  //   );
  //   return target?.submenu ?? null;
  // };
  // const tabs = getTabs();

  return (
    <div
      className={cn(
        'desktop:flex fixed right-4 hidden transform flex-col items-center gap-5',
        'top-[128px]',
      )}
      style={{
        zIndex: Z_INDEX.Cta,
      }}
    >
      {/* 전화 상담 버튼 */}
      <div className="bg-sub-darknavy relative h-[219px] w-40 rounded-xl text-white shadow-[0_4px_6px_0_rgba(0,0,0,0.25)]">
        <ImageOptimizer
          src="/images/icons/icon_cta_slash.svg"
          alt="slash"
          width={84}
          height={16}
          className="absolute top-0 right-5"
        />
        <div className="mt-10 flex flex-col items-center justify-center">
          <div className="mb-1.5 flex gap-0.5">
            <ImageOptimizer
              src="/images/icons/icon_cta_phone.svg"
              alt="phone"
              width={16}
              height={16}
            />
            <span className="typo-18 font-bold">대표번호</span>
          </div>
          <div
            className="typo-24 text-main-yellow mb-4 cursor-pointer font-extrabold hover:opacity-80"
            onClick={() => handleCall(DefaultCallNumber)}
          >
            {DefaultCallNumber}
          </div>
          <div className="mb-2 h-[72px] w-[136px] rounded-xl bg-[#FFFFFF26]">
            <Carousel
              setApi={setPcApi}
              opts={{
                align: 'center',
                loop: true,
                slidesToScroll: 1,
                containScroll: 'trimSnaps',
              }}
              plugins={[
                Autoplay({
                  delay: 3000,
                  stopOnMouseEnter: true,
                  stopOnInteraction: false,
                }),
              ]}
            >
              <CarouselContent className="mx-auto flex items-center">
                {stores.map((store, idx) => (
                  <CarouselItem
                    key={idx}
                    className="h-[72px] w-[136px] flex-shrink-0 pl-0"
                  >
                    <div className="flex h-full flex-col items-center justify-center space-x-0 px-0">
                      <span className="typo-14 mb-1.5 block truncate font-bold">
                        {store.name}
                      </span>
                      <span
                        className="text-main-yellow typo-20 block cursor-pointer font-extrabold hover:opacity-80"
                        onClick={() => handleCall(store.phoneNumber)}
                      >
                        {store.phoneNumber}
                      </span>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>

          {stores.length > 1 && (
            <div className="flex justify-center gap-2">
              <ImageOptimizer
                src="/images/icons/icon_cta_arrow_left.svg"
                alt="prev"
                width={22}
                height={22}
                className="cursor-pointer hover:opacity-70"
                // onClick={handlePrevStore}
                onClick={handlePrevClick}
              />
              <ImageOptimizer
                src="/images/icons/icon_cta_arrow_right.svg"
                alt="next"
                width={22}
                height={22}
                className="cursor-pointer hover:opacity-70"
                // onClick={handleNextStore}
                onClick={handleNextClick}
              />
            </div>
          )}
        </div>
      </div>

      <div className="relative mb-2 flex h-[244px] w-40 cursor-pointer flex-col items-center rounded-xl bg-[#F5DD5F] p-4">
        <ImageOptimizer
          src="/images/cta/bar.svg"
          alt="slash"
          width={160}
          height={24}
          className="absolute top-0 right-0 left-0 w-full rounded-tl-lg rounded-tr-lg"
        />
        {/* <div className="absolute top-0 right-0 left-0 h-4 w-full rounded-tl-lg rounded-tr-lg bg-[url(/images/cta/bar.png)] bg-repeat-x" /> */}
        <div className="mt-2.5 mb-3 flex h-[80px] w-[136px] flex-col items-center justify-center rounded-lg bg-[#FFFFFF73]">
          <ImageOptimizer
            src="/images/cta/emoji.png" // 적절한 웃는 이모티콘 이미지로 교체해야 합니다.
            alt="smile"
            width={22}
            height={22}
          />
          <p className="typo-14 text-level-2 mt-1 text-center leading-tight font-bold">
            언제든지 상담/문의
            <br />
            가능합니다.
          </p>
        </div>
        <div className="">
          <p className="typo-16 text-level-3 mb-3 text-center font-extrabold">
            - 매장 운영 시간 -
          </p>
          <div className="mb-3">
            <p className="typo-14 text-level-4 mb-1 text-start font-bold">
              평일
            </p>
            <p className="typo-20 text-level-3 text-center font-extrabold">
              09:00~21:00
            </p>
          </div>
          <div className="">
            <p className="typo-14 text-level-4 mb-1 text-start font-bold">
              주말
            </p>
            <p className="typo-20 text-level-3 text-center font-extrabold">
              10:00~17:00
            </p>
          </div>
        </div>
      </div>
      {/* 카카오톡 상담하기 버튼 */}
      <ImageOptimizer
        src="/images/cta/icon_cta_kakao.png"
        alt="kakao-talk"
        width={138}
        height={154}
        className="cursor-pointer transition-opacity hover:opacity-90"
        onClick={handleKakaoTalk}
      />

      {/* CSS 애니메이션 정의 */}
      <style jsx global>{`
        @keyframes slideInFromRight {
          0% {
            transform: translateX(100%);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .animate-slideInFromRight {
          animation: slideInFromRight 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SideCTA;
